import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { ScreenReaderOnly, Box, Image } from '@moonpig/launchpad-components'
import { styled } from '@moonpig/launchpad-utils'
import useEmblaCarousel from 'embla-carousel-react'
import { useGiftImageCarouselLocaleText } from '../../../GiftImageCarousel/GiftImageCarousel.locale'
import { useLocaleText } from '../../ProductTile.locale'
import { ProductImage } from '../../../../types'
import { useProductsLocaleText } from '../../../../locale'
import { NavigationDots } from './NavigationDots'
import { Controls } from './Controls'

const StyledSlide = styled.div`
  flex: 0 0 100%;
  min-width: 0;
  width: 100%;
  height: 100%;
  line-height: 0;
  overflow: hidden;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
`

const StyledViewport = styled.div`
  overflow: hidden;
  position: relative;
`

const StyledContainer = styled.div`
  display: flex;
  align-items: center;
  touch-action: pan-y pinch-zoom;
`

const StyledCarouselRoot = styled.div`
  position: relative;
`

const MAX_CAROUSEL_IMAGES = 6

const Carousel: FC<
  React.PropsWithChildren<{
    title: string
    isHovered: boolean
    onTapToCTA: () => void
  }>
> = ({ title, children, isHovered, onTapToCTA }) => {
  const localiseText = useGiftImageCarouselLocaleText()
  const t = useLocaleText()

  const [selectedIndex, setSelectedIndex] = useState(0)
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([])

  const dotsUlRef = useRef<HTMLUListElement>(null)

  const [emblaRef, emblaApi] = useEmblaCarousel({
    startIndex: 0,
    containScroll: 'trimSnaps',
    loop: false,
  })

  const childrenArray = useMemo(
    () => React.Children.toArray(children),
    [children],
  )
  const childrenCount = childrenArray.length

  useEffect(() => {
    if (emblaApi) return

    if (scrollSnaps.length === 0 && childrenCount > 0) {
      setScrollSnaps(childrenArray.map((_, index) => index))
    }
  }, [childrenArray, childrenCount, emblaApi, scrollSnaps.length])

  useEffect(() => {
    if (!emblaApi) return

    const syncFromEmbla = () => {
      setSelectedIndex(emblaApi.selectedScrollSnap())
      const snaps = emblaApi.scrollSnapList()
      setScrollSnaps(
        snaps.length > 0
          ? snaps.map((_, index) => index)
          : Array.from({ length: childrenCount }, (_, index) => index),
      )
    }

    emblaApi
      .on('select', syncFromEmbla)
      .on('pointerUp', syncFromEmbla)
      .on('settle', syncFromEmbla)
      .on('reInit', syncFromEmbla)

    syncFromEmbla()

    return () => {
      emblaApi
        .off('select', syncFromEmbla)
        .off('pointerUp', syncFromEmbla)
        .off('settle', syncFromEmbla)
        .off('reInit', syncFromEmbla)
    }
  }, [emblaApi, childrenCount])

  const handleViewportClick = useCallback(
    (e: React.MouseEvent) => {
      const target = e.target

      /* istanbul ignore next */
      if (!(target instanceof Element)) return

      const clickedSlide = target.closest('[data-carousel-slide]')
      if (!clickedSlide) return

      onTapToCTA()
      e.preventDefault()
      e.stopPropagation()
    },
    [onTapToCTA],
  )

  return (
    <Box zIndex={1} style={{ userSelect: 'none', WebkitUserSelect: 'none' }}>
      <StyledCarouselRoot
        role="region"
        aria-roledescription={t('carousel.description')}
        aria-label={localiseText('common.gift_image_carousel.root_title_text', {
          title,
        })}
        title={localiseText('common.gift_image_carousel.root_title_text', {
          title,
        })}
        data-testid="image-carousel-viewport"
      >
        <StyledViewport
          ref={emblaRef}
          data-carousel-viewport
          data-testid="image-carousel-viewport-inner"
          onClick={handleViewportClick}
        >
          <StyledContainer>{children}</StyledContainer>
        </StyledViewport>
        <Controls
          emblaApi={emblaApi}
          scrollSnaps={scrollSnaps}
          selectedIndex={selectedIndex}
          setScrollSnaps={setScrollSnaps}
          childrenArray={childrenArray}
          childrenCount={childrenCount}
          isHovered={isHovered}
        />
        <NavigationDots
          ref={dotsUlRef}
          items={scrollSnaps}
          selectedIndex={selectedIndex}
          isHovered={isHovered}
        />
      </StyledCarouselRoot>
    </Box>
  )
}

const mapProductImagesToCarouselImages = (
  productImages: ProductImage[],
): Array<ImageCarouselItem> => {
  return productImages.map(image => ({
    small: image.small.jpgUrl,
    medium: image.medium.jpgUrl,
    large: image.large.jpgUrl,
  }))
}

type ImageSizeKey = 'small' | 'medium' | 'large'
type ImageCarouselItem = Record<ImageSizeKey, string>

export const ImageCarousel: FC<{
  title: string
  productImages: ProductImage[]
  isHovered?: boolean
  onTapToCTA: () => void
}> = ({ title, productImages, isHovered, onTapToCTA }) => {
  const localeText = useProductsLocaleText()
  const carouselItems: Array<ImageCarouselItem> =
    mapProductImagesToCarouselImages(
      productImages.slice(0, MAX_CAROUSEL_IMAGES),
    )

  return (
    <Carousel title={title} isHovered={!!isHovered} onTapToCTA={onTapToCTA}>
      {carouselItems.map((carouselItem, index) => (
        <StyledSlide
          data-carousel-slide
          // eslint-disable-next-line react/no-array-index-key
          key={`${carouselItem.medium}-${index}`}
        >
          <Box position="relative">
            <Image
              data-testid="image-carousel-image"
              width="100%"
              src={carouselItem.medium}
              aria-hidden
              loading={index === 0 ? 'eager' : 'lazy'}
            />

            <ScreenReaderOnly>
              {localeText('product_image.image_label', title, index + 1)}
            </ScreenReaderOnly>
          </Box>
        </StyledSlide>
      ))}
    </Carousel>
  )
}
